import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiConfig } from '../config/api';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  role: string;
  is_active: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (userData: RegisterData) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
  validateToken: () => Promise<boolean>;
}

interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      // Step 1: Check for SSO token in URL query parameters (highest priority)
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const ssoToken = urlParams.get('token');
        const ssoRole = urlParams.get('role');

        if (ssoToken) {
          console.log('🔐 SSO: Token found in URL, processing SSO login...');
          
          try {
            // Store SSO token
            localStorage.setItem('access_token', ssoToken);
            setToken(ssoToken);

            // Check if we're in staging/production
            const isStagingOrProduction = 
              window.location.hostname.includes('stg-') || 
              window.location.hostname.includes('staging') ||
              (!window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1'));
            
            // Determine auth base URL
            let authBaseUrl: string;
            if (isStagingOrProduction) {
              authBaseUrl = '/api';
            } else {
              authBaseUrl = apiConfig.authServiceUrl || 'http://localhost:3001/api/v1';
              if (!authBaseUrl.includes('3001') && !authBaseUrl.startsWith('/')) {
                authBaseUrl = 'http://localhost:3001/api/v1';
              }
            }

            // Fetch user data from backend to validate token
            const endpoints = [
              `${authBaseUrl}/auth/me`,
              `${authBaseUrl.replace('/api/v1', '')}/auth/me`,
            ];

            let userData: User | null = null;
            for (const endpoint of endpoints) {
              try {
                console.log('🔐 SSO: Validating token with backend:', endpoint);
                const response = await fetch(endpoint, {
                  method: 'GET',
                  headers: {
                    'Authorization': `Bearer ${ssoToken}`,
                    'Content-Type': 'application/json',
                  },
                });

                if (response.ok) {
                  userData = await response.json();
                  console.log('✅ SSO: Token validated, user data:', userData);
                  
                  // If role is provided in URL, ensure it matches or update user data
                  if (ssoRole && userData) {
                    userData.role = ssoRole;
                  }
                  
                  // Store user data
                  localStorage.setItem('user', JSON.stringify(userData));
                  setUser(userData);
                  break;
                } else if (response.status === 404 && endpoint !== endpoints[endpoints.length - 1]) {
                  continue; // Try next endpoint
                } else {
                  console.warn('⚠️ SSO: Token validation failed with status:', response.status);
                  // If validation fails, clear the token
                  localStorage.removeItem('access_token');
                  setToken(null);
                }
              } catch (error) {
                console.error('❌ SSO: Error validating token:', error);
                if (endpoint === endpoints[endpoints.length - 1]) {
                  // Last endpoint failed, clear token
                  localStorage.removeItem('access_token');
                  setToken(null);
                }
              }
            }

            // Clean up URL parameters
            const newUrl = window.location.pathname + (window.location.hash || '');
            window.history.replaceState({}, '', newUrl);

            // Redirect to /app dashboard if not already there
            if (userData && window.location.pathname !== '/app') {
              console.log('🔐 SSO: Redirecting to /app dashboard');
              window.location.href = '/app';
              return; // Exit early, redirect will happen
            }

            // If we have user data, we're done
            if (userData) {
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error('❌ SSO: Error processing SSO login:', error);
            // Clear any partial data
            localStorage.removeItem('access_token');
            setToken(null);
          }
        }
      }

      // Step 2: Check for existing auth data in localStorage (existing functionality)
      const storedToken = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Function to validate token with backend
  const validateToken = async (): Promise<boolean> => {
    const currentToken = localStorage.getItem('access_token');
    console.log('🔍 Validating token:', currentToken ? 'Token exists' : 'No token found');
    
    if (!currentToken) {
      console.log('❌ No token found in localStorage');
      return false;
    }

    // Use authServiceUrl for token validation
    // For staging/production, use relative URLs; for localhost, use port 3001
    
    // Check if we're in staging/production
    const isStagingOrProduction = typeof window !== 'undefined' && 
      (window.location.hostname.includes('stg-') || 
       window.location.hostname.includes('staging') ||
       (!window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1')));
    
    // For staging/production, ALWAYS use relative URL regardless of apiConfig
    let authBaseUrl: string;
    if (isStagingOrProduction) {
      authBaseUrl = '/api';
      console.log('🌐 [STAGING] Using relative URL for token validation:', authBaseUrl);
    } else {
      // For localhost, use apiConfig or default
      authBaseUrl = apiConfig.authServiceUrl || 'http://localhost:3001/api/v1';
      
      // Only enforce port 3001 for localhost
      if (!authBaseUrl.includes('3001') && !authBaseUrl.startsWith('/')) {
        console.warn('⚠️ Auth service should use port 3001 for localhost, forcing correct URL');
        authBaseUrl = 'http://localhost:3001/api/v1';
      }
    }
    
    const endpoints = [
      `${authBaseUrl}/auth/me`,
      `${authBaseUrl.replace('/api/v1', '')}/auth/me`,
    ];

    for (const endpoint of endpoints) {
      try {
        console.log('🌐 Making request to:', endpoint);
        console.log('🔑 Using token:', currentToken.substring(0, 20) + '...');
        
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${currentToken}`,
            'Content-Type': 'application/json',
          },
        });

        console.log('📡 Response status:', response.status);

        if (response.ok) {
          // Token is valid, update user data if needed
          const userData = await response.json();
          console.log('✅ Token is valid, user data:', userData);
          setUser(userData);
          return true;
        } else if (response.status === 401) {
          console.log('❌ Token is invalid (401), clearing tokens');
          // Token is invalid, clear it
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          return false;
        } else if (response.status === 404 && endpoint !== endpoints[endpoints.length - 1]) {
          continue; // Try next endpoint
        }
      } catch (error) {
        if (endpoint === endpoints[endpoints.length - 1]) {
          console.error('❌ Error validating token:', error);
        }
        // Continue to next endpoint if not the last one
        if (endpoint !== endpoints[endpoints.length - 1]) {
          continue;
        }
        return false;
      }
    }
    
    return false;
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    // CRITICAL: Always use authServiceUrl for authentication
    // For staging/production, use relative URLs (e.g., '/api')
    // For localhost, use full URLs with port 3001
    
    // Check if we're in staging/production (using relative URLs)
    const isStagingOrProduction = typeof window !== 'undefined' && 
      (window.location.hostname.includes('stg-') || 
       window.location.hostname.includes('staging') ||
       (!window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1')));
    
    // For staging/production, ALWAYS use relative URL regardless of apiConfig
    let authBaseUrl: string;
    if (isStagingOrProduction) {
      authBaseUrl = '/api';
      console.log('🌐 [STAGING] Using relative URL for auth service:', authBaseUrl);
    } else {
      // For localhost, use apiConfig or default
      authBaseUrl = apiConfig.authServiceUrl || 'http://localhost:3001/api/v1';
      
      // Only enforce port 3001 for localhost
      if (!authBaseUrl.includes('3001') && !authBaseUrl.startsWith('/')) {
        console.error('❌ ERROR: Auth service should use port 3001 for localhost, but got:', authBaseUrl);
        console.log('✅ Forcing correct auth service URL: http://localhost:3001/api/v1');
        authBaseUrl = 'http://localhost:3001/api/v1';
      }
    }
    
    // Log the URLs being used for debugging
    console.log('🔍 Login Configuration:', {
      authServiceUrl: apiConfig.authServiceUrl,
      authBaseUrl: authBaseUrl,
      baseUrl: apiConfig.baseUrl,
      configEnvironment: apiConfig.environment,
    });
    
    // Try multiple endpoint variations in case backend uses different paths
    // ALWAYS prioritize authServiceUrl (port 3001) - this is the correct auth service
    const endpoints = [
      // Primary: Use authServiceUrl with /api/v1/auth/login
      `${authBaseUrl}/auth/login`,                                    // http://localhost:3001/api/v1/auth/login (PRIMARY)
      // Alternative paths on auth service
      `${authBaseUrl.replace('/api/v1', '')}/auth/login`,             // http://localhost:3001/auth/login
      `${authBaseUrl}/login`,                                          // http://localhost:3001/api/v1/login
      `${authBaseUrl.replace('/api/v1', '')}/login`,                  // http://localhost:3001/login
    ];
    
    // Log all endpoints we'll try
    console.log('🔐 Login endpoints to try:', endpoints);

    for (const endpoint of endpoints) {
      try {
        console.log('🔐 Attempting login to:', endpoint);
        
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password }),
        });

        console.log('📡 Login response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('✅ Login successful via:', endpoint);
          console.log('🔑 Access token:', data.access_token ? data.access_token.substring(0, 20) + '...' : 'No token');
          console.log('👤 User data:', data.user);
          
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          
          setToken(data.access_token);
          setUser(data.user);
          return true;
        } else {
          // If it's a 401/403, the endpoint exists but credentials are wrong
          if (response.status === 401 || response.status === 403) {
            const errorData = await response.json().catch(() => ({ detail: 'Invalid credentials' }));
            console.error('❌ Login failed - Invalid credentials');
            throw new Error(errorData.detail || 'Invalid email or password');
          }
          // For 404, try next endpoint
          if (response.status === 404 && endpoint !== endpoints[endpoints.length - 1]) {
            console.log(`⚠️ Endpoint ${endpoint} returned 404, trying next...`);
            continue;
          }
          // For other errors, try to parse and throw
          const errorData = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
          throw new Error(errorData.detail || `Login failed with status ${response.status}`);
        }
      } catch (error: any) {
        const isNetworkError = 
          error?.message?.includes('Network request failed') ||
          error?.message?.includes('Failed to fetch') ||
          error?.message?.includes('ERR_CONNECTION_TIMED_OUT') ||
          error?.message?.includes('ERR_CONNECTION_REFUSED') ||
          error?.name === 'TypeError';
        
        if (isNetworkError) {
          console.warn(`⚠️ Network error on ${endpoint}:`, error?.message);
          if (endpoint !== endpoints[endpoints.length - 1]) {
            console.log('🔄 Trying next endpoint...');
            continue;
          } else {
            // Last endpoint failed - provide helpful error
            const isLocalhost = endpoint.includes('localhost') || endpoint.includes('127.0.0.1');
            if (!isLocalhost) {
              throw new Error(
                `Cannot connect to server at ${endpoint}.\n\n` +
                `Please ensure:\n` +
                `1. Backend services are running on localhost\n` +
                `2. Use npm run start:local to force localhost mode\n` +
                `3. Check that auth service is running on port 3001`
              );
            } else {
              throw new Error(
                `Cannot connect to localhost auth service.\n\n` +
                `Please ensure:\n` +
                `1. Auth service is running on http://localhost:3001\n` +
                `2. Backend is listening on 0.0.0.0 (not just localhost)\n` +
                `3. No firewall is blocking port 3001`
              );
            }
          }
        }
        // For non-network errors (like 401), throw immediately
        console.error('❌ Login error:', error?.message);
        throw error;
      }
    }
    
    return false;
  };

  const register = async (userData: RegisterData): Promise<boolean> => {
    // Try multiple endpoints in case auth is on a different service
    const authBaseUrl = (apiConfig as any).authServiceUrl || apiConfig.baseUrl;
    const endpoints = [
      `${authBaseUrl}/auth/register`,
      `${apiConfig.baseUrl}/auth/register`,
      `${authBaseUrl.replace('/api/v1', '')}/auth/register`,
      `${apiConfig.baseUrl.replace('/api/v1', '')}/auth/register`,
    ];

    for (const endpoint of endpoints) {
      try {
        console.log('🔐 Attempting registration to:', endpoint);
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...userData,
            role: 'customer', // Explicitly set customer role for customer-web registrations
          }),
        });

        if (response.ok) {
          const data = await response.json();
          console.log('✅ Registration successful via:', endpoint);
          
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          
          setToken(data.access_token);
          setUser(data.user);
          return true;
        } else {
          if (response.status === 404 && endpoint !== endpoints[endpoints.length - 1]) {
            console.log(`⚠️ Endpoint ${endpoint} returned 404, trying next...`);
            continue;
          }
          const errorData = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
          throw new Error(errorData.detail || 'Registration failed');
        }
      } catch (error: any) {
        const isNetworkError = 
          error?.message?.includes('Network request failed') ||
          error?.message?.includes('Failed to fetch');
        
        if (isNetworkError && endpoint !== endpoints[endpoints.length - 1]) {
          console.log(`⚠️ Network error on ${endpoint}, trying next...`);
          continue;
        }
        console.error('❌ Registration error:', error?.message);
        throw error;
      }
    }
    
    return false;
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const isAuthenticated = !!user && !!token;

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    isAuthenticated,
    validateToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
